import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, CheckCircle2, Boxes, MapPin, Download, Package, TrendingDown, TrendingUp, ArrowRight, Warehouse } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import api, { API } from "@/api";
import TopBar from "@/components/TopBar";
import ManagementCarousel from "@/components/ManagementCarousel";
import BestOfDay from "@/components/BestOfDay";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Phase 14: Management dashboard with comparison KPIs + fall-in-sales alerts
const Stat = ({ icon: Icon, label, value, color, testid, onClick, delta, hint }) => {
  const interactive = !!onClick;
  const Wrapper = interactive ? "button" : "div";
  return (
    <Wrapper
      onClick={onClick}
      data-testid={testid}
      className={`stat-card text-left transition ${interactive ? "hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.99] cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`w-10 h-10 rounded-xl ${color} grid place-items-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {delta !== undefined && delta !== null && (
          <span className={`text-[11px] font-bold flex items-center gap-0.5 ${delta >= 0 ? "text-[#34C759]" : "text-[#FF3B30]"}`}>
            {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {delta >= 0 ? "+" : ""}{delta}%
          </span>
        )}
      </div>
      <div className="text-xs text-[#86868B] uppercase tracking-wider">{label}</div>
      <div className="heading-font text-2xl font-bold text-[#1D1D1F] mt-1">{value}</div>
      {hint && <div className="text-[10px] text-[#86868B] mt-1">{hint}</div>}
    </Wrapper>
  );
};

const pctDelta = (current, previous) => {
  if (!previous || previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
};

export default function ManagementDashboard() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [byProduct, setByProduct] = useState([]);
  const [salesmenPerf, setSalesmenPerf] = useState([]);
  const [depotStock, setDepotStock] = useState(null);
  const [liveTracking, setLiveTracking] = useState(null);
  const [inactiveAlerts, setInactiveAlerts] = useState([]);

  useEffect(() => {
    api.get("/dashboard/management").then(r => setData(r.data));
    api.get("/dashboard/units-breakdown", { params: { scope: "today", group_by: "product" } })
      .then(r => setByProduct(r.data?.rows || []));
    api.get("/dashboard/units-breakdown", { params: { scope: "today", group_by: "salesman" } })
      .then(r => setSalesmenPerf((r.data?.rows || []).map(s => ({ id: s.id, name: s.name, units_sold: s.units, total_orders: s.orders }))));
    // Phase G: depot summary + live tracking + inactivity alerts; refresh tracking every 30s
    const refreshLive = () => {
      api.get("/depot/stock/summary").then(r => setDepotStock(r.data)).catch(() => {});
      api.get("/tracking/live").then(r => setLiveTracking(r.data)).catch(() => {});
      api.get("/alerts/inactivity").then(r => setInactiveAlerts(r.data?.alerts || [])).catch(() => {});
    };
    refreshLive();
    const id = setInterval(refreshLive, 30000);
    return () => clearInterval(id);
  }, []);

  if (!data) return <div className="min-h-screen grid place-items-center text-[#86868B]">{t("loading")}</div>;

  const downloadCsv = (path, filename) => {
    const token = localStorage.getItem("xtra_token");
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        toast.success("Downloaded");
      })
      .catch(() => toast.error("Download failed"));
  };

  const today = data.today || {};
  const trend = (data.trend_7d || []).map(d => ({ ...d, label: d.date.slice(5) }));
  const cmp = data.comparisons || {};
  const fall = data.fall_alerts || {};

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10">
      <TopBar title={t("company_performance") || "Company Performance"} />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6 fade-up">
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <button data-testid="export-orders-btn" onClick={() => downloadCsv("/reports/orders.csv", "orders.csv")} className="bg-white hover:bg-[#0055FF] hover:text-white text-[#0055FF] border border-[#0055FF] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2 transition">
            <Download className="w-4 h-4" /> Orders CSV
          </button>
          <button data-testid="export-units-btn" onClick={() => downloadCsv("/reports/units-summary.csv", "boxes-summary.csv")} className="bg-white hover:bg-[#0055FF] hover:text-white text-[#0055FF] border border-[#0055FF] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2 transition">
            <Download className="w-4 h-4" /> Boxes Summary
          </button>
          <button data-testid="export-units-by-product-btn" onClick={() => downloadCsv("/reports/units-by-product.csv", "boxes-by-product.csv")} className="bg-white hover:bg-[#0055FF] hover:text-white text-[#0055FF] border border-[#0055FF] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2 transition">
            <Download className="w-4 h-4" /> Boxes by Product
          </button>
          <button data-testid="mg-stock-report-btn" onClick={() => nav("/management/stock-report")} className="bg-white hover:bg-[#5856D6] hover:text-white text-[#5856D6] border border-[#5856D6] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2 transition">
            <Warehouse className="w-4 h-4" /> {t("stock_received")}
          </button>
          <button data-testid="mg-attendance-btn" onClick={() => nav("/management/attendance")} className="bg-white hover:bg-[#34C759] hover:text-white text-[#34C759] border border-[#34C759] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2 transition">
            <CheckCircle2 className="w-4 h-4" /> {t("attendance") || "Attendance"}
          </button>
          <button data-testid="mg-attendance-report-btn" onClick={() => nav("/management/attendance-report")} className="bg-white hover:bg-[#5856D6] hover:text-white text-[#5856D6] border border-[#5856D6] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2 transition">
            <Download className="w-4 h-4" /> {t("attendance_report") || "Attendance Report"}
          </button>
          <button data-testid="mg-route-history-btn" onClick={() => nav("/management/route-history")} className="bg-white hover:bg-[#0055FF] hover:text-white text-[#0055FF] border border-[#0055FF] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2 transition">
            <MapPin className="w-4 h-4" /> {t("route_history") || "Route History"}
          </button>
        </div>

        {/* Phase D: Auto-rotating screensaver carousel — tap to pause, tap paused to drill down */}
        <ManagementCarousel
          today={today}
          byDistrict={data.by_district || []}
          byProduct={byProduct}
          performance={[...salesmenPerf, ...(data.managers || []).map(m => ({...m, role: "manager"}))].sort((a,b)=>(b.units_sold||0)-(a.units_sold||0))}
          depotStock={depotStock}
          liveTracking={liveTracking}
        />

        {/* Phase G: Inactivity alerts banner */}
        {inactiveAlerts.length > 0 && (
          <button
            data-testid="mg-inactivity-alert"
            onClick={() => nav("/management/live-tracking")}
            className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl p-4 shadow-lg flex items-center gap-3 hover:shadow-xl active:scale-[0.99] transition text-left"
          >
            <div className="w-10 h-10 rounded-2xl bg-white/15 grid place-items-center shrink-0">
              <span className="text-xl">⚠️</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">{t("inactivity_alerts")}</div>
              <div className="heading-font font-bold">
                {inactiveAlerts.length} {lang === "ta" ? "பேர் செயல்பாடில்லை" : "people inactive (>15 min)"}
              </div>
              <div className="text-xs opacity-80 truncate mt-0.5">
                {inactiveAlerts.slice(0, 3).map(a => a.name).join(", ")}{inactiveAlerts.length > 3 ? "…" : ""}
              </div>
            </div>
            <span className="text-xs font-bold opacity-80">→</span>
          </button>
        )}

        {/* Phase D: Best Sales of the Day */}
        <BestOfDay salesmen={salesmenPerf} managers={data.managers || []} />

        {/* KPIs — Phase D: navigate to full-screen drilldowns instead of opening modals */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat icon={Truck}        color="bg-[#0055FF]" label="Orders"            value={today.total_orders || 0} testid="mg-stat-orders"     onClick={() => nav("/management/orders")} />
          <Stat icon={CheckCircle2} color="bg-[#34C759]" label="Delivered Today"   value={today.delivered || 0}    testid="mg-stat-delivered"  onClick={() => nav("/management/delivered")} />
          <Stat icon={Package}      color="bg-[#FF9500]" label="Pending"           value={today.pending || 0}      testid="mg-stat-pending"    onClick={() => nav("/management/pending")} />
          <Stat icon={Boxes}        color="bg-[#5856D6]" label="Boxes Sold"        value={today.units_sold || 0}   testid="mg-stat-units"      onClick={() => nav("/management/boxes")} />
          <Stat icon={MapPin}       color="bg-amber-500" label="Districts"         value={data.by_district?.length || 0} testid="mg-stat-districts" onClick={() => nav("/management/districts")} />
        </div>

        {/* Phase 14: Comparison row — Today vs Yesterday / Week vs Last / Month vs Last (units only) */}
        {cmp && (cmp.today || cmp.week || cmp.month) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="mg-comparisons">
            <div className="bg-white rounded-2xl p-5 soft-shadow">
              <div className="text-xs text-[#86868B] uppercase tracking-wider font-bold">Today vs Yesterday</div>
              <div className="heading-font text-3xl font-bold mt-1.5 text-[#1D1D1F]">{cmp.today?.current ?? 0}</div>
              <div className="text-xs text-[#86868B] mt-0.5">vs {cmp.today?.previous ?? 0} yesterday</div>
              <DeltaBadge current={cmp.today?.current} previous={cmp.today?.previous} />
            </div>
            <div className="bg-white rounded-2xl p-5 soft-shadow">
              <div className="text-xs text-[#86868B] uppercase tracking-wider font-bold">This Week vs Last</div>
              <div className="heading-font text-3xl font-bold mt-1.5 text-[#1D1D1F]">{cmp.week?.current ?? 0}</div>
              <div className="text-xs text-[#86868B] mt-0.5">vs {cmp.week?.previous ?? 0} last week</div>
              <DeltaBadge current={cmp.week?.current} previous={cmp.week?.previous} />
            </div>
            <div className="bg-white rounded-2xl p-5 soft-shadow">
              <div className="text-xs text-[#86868B] uppercase tracking-wider font-bold">This Month vs Last</div>
              <div className="heading-font text-3xl font-bold mt-1.5 text-[#1D1D1F]">{cmp.month?.current ?? 0}</div>
              <div className="text-xs text-[#86868B] mt-0.5">vs {cmp.month?.previous ?? 0} last month</div>
              <DeltaBadge current={cmp.month?.current} previous={cmp.month?.previous} />
            </div>
          </div>
        )}

        {/* Phase 14: Fall-in-sales alerts (always visible — shows 'No alerts' when nothing fell). */}
        <div className={`${(fall.outlets?.length || fall.salesmen?.length || fall.products?.length) ? "bg-red-50 border-red-200" : "bg-white border-gray-200"} border rounded-2xl p-5`} data-testid="mg-fall-alerts">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className={`w-5 h-5 ${(fall.outlets?.length || fall.salesmen?.length || fall.products?.length) ? "text-red-600" : "text-[#86868B]"}`} />
            <h3 className={`heading-font font-bold text-lg ${(fall.outlets?.length || fall.salesmen?.length || fall.products?.length) ? "text-red-900" : "text-[#1D1D1F]"}`}>Fall in Sales — Alerts (vs prior week)</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FallList title="Outlets" items={fall.outlets} testid="fall-outlets" />
            <FallList title="Salesmen" items={fall.salesmen} testid="fall-salesmen" />
            <FallList title="Products" items={fall.products} testid="fall-products" />
          </div>
        </div>

        {/* Phase F: Bottom drilldown icons removed — screensaver carousel + stat tiles + best-of-day are the canonical entry points. */}

        {/* Trend Chart — UNITS ONLY */}
        <div className="bg-white rounded-2xl p-5 soft-shadow">
          <h3 className="heading-font font-bold text-lg mb-4">Last 7 Days • Boxes Sold</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#86868B" }} />
                <YAxis tick={{ fontSize: 12, fill: "#86868B" }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eee" }} />
                <Line type="monotone" dataKey="units" name="Boxes Sold" stroke="#5856D6" strokeWidth={3} dot={{ fill: "#5856D6", r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* District Performance — UNITS ONLY */}
        <div className="bg-white rounded-2xl p-5 soft-shadow">
          <h3 className="heading-font font-bold text-lg mb-4">District Performance (Today) • Boxes Sold</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_district || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#86868B" }} />
                <YAxis tick={{ fontSize: 12, fill: "#86868B" }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eee" }} />
                <Bar dataKey="units_sold" name="Boxes Sold" fill="#5856D6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Manager performance — UNITS ONLY */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h2 className="heading-font font-bold text-xl">Managers (Today)</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Orders</th>
                  <th className="px-4 py-3">Delivered</th>
                  <th className="px-4 py-3">Boxes Sold</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.managers?.map(m => (
                  <tr key={m.id}>
                    <td className="px-4 py-3 font-semibold heading-font">{m.name}</td>
                    <td className="px-4 py-3">{m.total_orders}</td>
                    <td className="px-4 py-3"><span className="pill bg-green-50 text-green-700">{m.delivered}</span></td>
                    <td className="px-4 py-3 font-bold">{m.units_sold || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const DeltaBadge = ({ current, previous }) => {
  const d = pctDelta(current || 0, previous || 0);
  return (
    <div className={`mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${d >= 0 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
      {d >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {d >= 0 ? "+" : ""}{d}%
    </div>
  );
};

const FallList = ({ title, items, testid }) => (
  <div className="bg-white rounded-xl p-3" data-testid={testid}>
    <div className="text-xs font-bold text-red-700 uppercase tracking-wider mb-2">{title}</div>
    {!items || items.length === 0 ? (
      <div className="text-xs text-[#86868B]">No alerts</div>
    ) : (
      <ul className="space-y-1.5 text-sm">
        {items.slice(0, 5).map((it, i) => (
          <li key={i} className="flex items-center justify-between gap-2">
            <span className="font-semibold truncate">{it.name}</span>
            <span className="text-red-600 text-xs font-bold flex-shrink-0">↓{Math.abs(it.delta_pct)}%</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);


